import React, { useEffect, useState } from 'react'
import { convertSearchSpringProductPricingToMarketPrice, isSearchSpringProductResponse, standardSearch } from '../../helpers/searchSpringHelpers'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'
import { Result, Pagination, Facet, Sorting, Payload, SearchSpringProductResponse } from '../../search-spring/types/types'
import { extractFilterParamsFromURL } from '../../search-spring-page/helpers/extractParamsFromURL'
import PaginationService from '../../search-spring-page/components/PaginationService'
import CollectionPageHeader from './CollectionPageHeader'

declare global {
  interface Window {
    collectionSEOText: string
  }
}

declare var Shopify: { currency: { active: string } }

function CollectionPageInit() {
  const [products, setProducts] = useState<Result[]>([])
  const [pagination, setPagination] = useState<Pagination>()
  const [filters, setFilters] = useState<Facet[]>()
  const [sorting, setSorting] = useState<Sorting>()
  const windowSize = useWindowSize()
  const baseResultsPerPage = windowSize.width <= 640 ? CD_STATE.searchspring.numberOfProductsMobile : CD_STATE.searchspring.numberOfProductsDesktop
  const [payload, setPayload] = useState<Payload>({})
  const [activeFilters, setActiveFilters] = useState<number>(0)
  const [error, setError] = useState(false)
  const [displayMore, setDisplayMore] = useState(false)
  const [collectionExtraProductBuffer, setCollectionExtraProductBuffer] = useState<number>(0)
  const collectionHandle = CD_STATE.searchspring.searchTerms
  const searchType = CD_STATE.searchspring.searchType

  const getExtraProductBuffer = (isMobile: boolean) => {
    if (isMobile) return 0
    const disrupter = CD_STATE?.collectionDisrupter
    if (!disrupter) return 0
    const disrupterData = [
      disrupter?.collectionDisrupterData1,
      disrupter?.collectionDisrupterData2,
      disrupter?.collectionDisrupterData3,
    ]
    const tilesNeedingBuffer = disrupterData.filter((data) => {
      const height = (data?.height ?? 'screen').toLowerCase()
      const width = (data?.width ?? '50%').toLowerCase()
      return height === 'card' && width === '50%'
    })
    return tilesNeedingBuffer.length % 2 === 1 ? 2 : 0
  }
  
  const getCollectionPageResults = async () => {
    const filterAndPageParams = extractFilterParamsFromURL()
    try {
      const extraProductBuffer = getExtraProductBuffer(windowSize.width <= 640)
      let resultsPerPage = baseResultsPerPage + extraProductBuffer
      // Payload won't update in time so we set it again here then update it.
      const updatedPayload: Payload = {
        ...payload,
        resultsPerPage,
        activeFilters: filterAndPageParams.filterArray,
      }

      if (filterAndPageParams.page) {
        /*
          This means a url was pasted in as a page param can only ever exist in this context,
          so we will append it to our payload. For the first search we also need to adjust the 
          amount of products we get so it matches the page  
        */ 
        updatedPayload.page = filterAndPageParams.page
      } else {
        delete updatedPayload.page
      }

      setPayload(updatedPayload)
      setCollectionExtraProductBuffer(Math.max(0, resultsPerPage - baseResultsPerPage))

      /*
        initPageParam - is used to multiply the resuls per page by the number of the page param in the URL.
        Because on the first load if there is page=3 in the url then we expect 60 products on desktop, 
        however as default we always 20 (Or whatever S&S sets in the theme customizer) products per search.  
      */ 
      const response = await standardSearch({ searchQuery: collectionHandle, searchType: searchType, payload: updatedPayload, initPageParam: updatedPayload.page ?? null })
      if (isSearchSpringProductResponse(response)) {
        if (Shopify.currency.active != "AUD") {
          const newproductData: SearchSpringProductResponse = await convertSearchSpringProductPricingToMarketPrice(response)
          // if there is no results from the storefront during conversion it means there was an error. So as a fallback
          // We will use the AU results, this will mean wrong product prices on the PLP HOWEVER
          // The Product page and cart will have correct prices
          setProducts(newproductData.results || response.results)
        } else {
          setProducts(response.results)
        }
        setPagination(response.pagination)
        setFilters(response.facets)
        setSorting(response.sorting)
        setActiveFilters(response.filterSummary.length)
      } else {
        setError(true)
        console.error("Unexpected response structure:", response)
      }
    } catch (error) {
      setError(true)
      console.error("Error fetching search page results:", error)
    }
  }

  useEffect(() => {
    const handleUrlChange = () => {
      // When doing a search by a filter need to delete the page params as page should reset when touching the filters
      const params = new URLSearchParams(new URL(location.href).search)
      const url = new URL(location.href)
      if (params.has('page')) {
        params.delete('page')
        history.replaceState(null, '', url.pathname + '?' + params.toString())
      }
      getCollectionPageResults()
    }

    window.addEventListener('urlChange', handleUrlChange as EventListener)

    return () => {
      window.removeEventListener('urlChange', handleUrlChange as EventListener)
    }
  }, [])

  useEffect(() => {
    getCollectionPageResults()
  },[])

  const ComponentHandler = (): JSX.Element => {
    const moreIndex = CD_STATE.collectionDisrupter.collectionSEOText.indexOf('[more]')
    const hasMore = moreIndex !== -1
    const textBeforeMore = hasMore ? CD_STATE.collectionDisrupter.collectionSEOText.slice(0, moreIndex) : CD_STATE.collectionDisrupter.collectionSEOText
    const textAfterMore = hasMore ? CD_STATE.collectionDisrupter.collectionSEOText.slice(moreIndex + 6) : ""
    if (products && pagination && filters && sorting) {
      return <div className="block">
          <CollectionPageHeader filters={filters} sorting={sorting} triggerSearch={getCollectionPageResults} activeFilters={activeFilters}/>
          <div className={`${CD_STATE.collectionDisrupter?.collectionSEOText ? 'mb-20' : ''}`}>
            <PaginationService
              pagination={pagination}
              products={products}
              searchQuery={collectionHandle}
              payload={payload}
              isSearchPage={false}
              collectionExtraProductBuffer={collectionExtraProductBuffer}
            />
          </div>
          {CD_STATE.collectionDisrupter?.collectionSEOText && (
            <div className="wrapper-link-custom-underline mb-20 mx-5 lg:my-[120px] lg:w-1/2 w-auto [&_p]:inline  [&_p]:font-calibre [&_a]:font-calibre [&_p]:text-xs [&_a]:text-xs [&_p]:font-light [&_a]:font-light [&_p]:text-subdued [&_a]:text-subdued">
              {textBeforeMore && (
                <div className="inline [&_div]:inline [&_p]:mb-2 [&_p:last-child]:mb-0"
                dangerouslySetInnerHTML={{ __html: textBeforeMore + (hasMore && !displayMore ? '...' : '') }}></div>
              )}
              {textAfterMore && (
                <div className={`${displayMore ? '[&_p]:mb-2 [&_p:last-child]:mb-0 inline' : 'u-visually-hidden'} [&_p]:inline`}>
                  <p dangerouslySetInnerHTML={{ __html: textAfterMore }}></p>
                </div>
              )}
              {hasMore && (
                <button className={`${displayMore ? 'mt-2.5 block' : 'inline'}  link-custom-underline text-xs text-subdued font-calibre font-light`} onClick={() => setDisplayMore(!displayMore)}>
                  &nbsp;{displayMore ? CD_STATE.collectionDisrupter?.readLess : CD_STATE.collectionDisrupter?.readMore}
                </button>
              )}
            </div>
          )}
        </div>
    }

    // if error display error 
    if (error) {
      return <div className="text-xl font-bstSpyre mt-4 text-center mx-5">Sorry, This Collection cannot load right now. Please try again later or contact us if this issue persists.</div>
    }
    
    // TODO: implement skeleton loaders
    return <div className='min-h-[1080px]'></div>
  }

  return (
    <ComponentHandler />
  )
}

export default CollectionPageInit
